import React, { useMemo } from "react";
import { Chip, ChipProps, styled, Typography, TypographyProps } from "@mui/material";
import colors from "@/utils/colorsV2";
import UIText from "../text/UIText";
import UIChipProps from "./UIChipProps";
import UITextProps from "../text/UITextProps";
import { Props } from "mui-modal-provider";

type ChipType = UIChipProps["type"];
type TextColorType = UITextProps["color"];

const BULLET_DEFAULT_WIDTH = "calc(8ch + 40px)";

const colorStyles: Record<
  NonNullable<ChipType>,
  { backgroundColor: string; borderColor: string; color: string }
> = {
  success: { backgroundColor: colors.green2, borderColor: colors.green1, color: colors.success700 },
  primary: { backgroundColor: colors.grayLM50, borderColor: colors.grayLM200, color: colors.grayLM700 },
  secondary: { backgroundColor: colors.asertaOrange, borderColor: colors.asertaOrange, color: colors.grayLM700 },
  info: { backgroundColor: colors.blue4, borderColor: colors.blue3, color: colors.blue5 },
  error: { backgroundColor: colors.red2, borderColor: colors.red1, color: colors.red3 },
  warning: { backgroundColor: "#FFFAEB", borderColor: "#FEDF89", color: "#F79009" },
  purple: { backgroundColor: colors.utility_purple_50, borderColor: colors.utility_purple_200, color: colors.utility_purple_700 },
};

const textColors: Record<NonNullable<ChipType>, TextColorType> = {
  success: "success",
  primary: "gray",
  secondary: "white",
  info: "blue5",
  error: "red3",
  warning: "warning",
  purple: "purple",
};

interface ColorChipProps extends ChipProps {
  $hasBullet?: boolean;
  $tableMode?: boolean;
  $tableLines?: 1 | 2 | 3;
  $tableFullWidth?: boolean;
  $tableTextAlign?: "left" | "center" | "right";
  $forceOneLineOnMobile?: boolean;
  $bulletWidth?: string; // ancho fijo cuando hay bullet
}

// ...imports iguales

// (sin cambios) colorStyles, textColors, tipos, etc.

const ColorChip = styled(Chip, {
  shouldForwardProp: (p) =>
    ![
      "color",
      "$hasBullet",
      "$tableMode",
      "$tableLines",
      "$tableFullWidth",
      "$tableTextAlign",
      "$forceOneLineOnMobile",
      "$bulletWidth",
    ].includes(p as string),
})<ColorChipProps>(
  ({
    color = "primary",
    $hasBullet,
    $tableMode = false,
    $tableLines = 2,
    $tableFullWidth = false,
    $tableTextAlign = "center",
    $forceOneLineOnMobile = false,
    $bulletWidth = BULLET_DEFAULT_WIDTH,
  }) => {
    const cs = (colorStyles as any)[color] ?? colorStyles.primary;
    const fg = cs.color;
    const bg = cs.backgroundColor;
    const bd = cs.borderColor;

    const base = {
      // Fondo/borde/fg desde tu paleta
      backgroundColor: bg,
      borderColor: bd,
      borderWidth: 1,
      borderStyle: "solid",

      // ⬆️ Especificidad para que gane sobre el estilo default del Chip (especialmente outlined)
      "&&": { color: fg },

      fontSize: 12,
      lineHeight: 1.25,
      minHeight: 24,
      boxSizing: "border-box" as const,
      transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",

      // Asegurar herencia del color en hijos
      "& .MuiChip-label, & .MuiChip-icon": { color: "inherit" },

      "& .MuiChip-icon": {
        marginLeft: 2,
        marginRight: 4,
        fontSize: 14,
        flex: "0 0 auto",
        "@media (max-width: 768px)": {
          fontSize: 12,
          marginLeft: 1,
          marginRight: 3,
        },
      },
    };

    // === Bullet: ancho fijo y una línea centrada ===
    if ($hasBullet) {
      return {
        ...base,
        width: $bulletWidth,
        maxWidth: $bulletWidth,
        "& .MuiChip-label": {
          padding: "0 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
          gap: 4,
          "@media (max-width: 768px)": { padding: "0 6px" },
          "@media (max-width: 480px)": { padding: "0 4px", fontSize: 11 },
        },
      };
    }

    if (!$tableMode) {
      // === Normal (1 línea, compacto) ===
      return {
        ...base,
        width: "fit-content",
        maxWidth: "100%",
        "& .MuiChip-label": {
          padding: "0 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
          gap: 4,
          "@media (max-width: 768px)": { padding: "0 6px" },
          "@media (max-width: 480px)": { padding: "0 4px", fontSize: 11 },
        },
      };
    }

    // === Table mode (multi-línea 1..3) ===
    const lines = Math.min(Math.max($tableLines ?? 2, 1), 3);

    return {
      ...base,
      minWidth: "98px",
      width: $tableFullWidth ? "100%" : "fit-content",
      maxWidth: "100%",
      "& .MuiChip-label": {
        padding: "0 8px",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: lines,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        textAlign: $tableTextAlign,
        gap: 4,

        "@media (max-width: 768px)": {
          WebkitLineClamp: $forceOneLineOnMobile ? 1 : Math.min(lines, 2),
          whiteSpace: $forceOneLineOnMobile ? "nowrap" : "normal",
          padding: "0 6px",
        },
        "@media (max-width: 480px)": {
          fontSize: 11,
          padding: "0 4px",
        },
      },
      "@media (max-width: 768px)": { fontSize: 11, minHeight: 22 },
      "@media (max-width: 480px)": { fontSize: 10, minHeight: 20 },
    };
  }
);

// ...Props sin cambios

const UIChip: React.FC<Props> = ({
  type = "primary",
  label,
  icon,
  sxText,
  tableMode = false,
  tableLines = 2,
  tableFullWidth = false,
  tableTextAlign = "center",
  tableForceOneLineOnMobile = false,
  useTypography = false,
  typoProps,
  bulletWidth = BULLET_DEFAULT_WIDTH,
}) => {
  const hasBullet = typeof label === "string" && label.trimStart().startsWith("•");

  // Mantén el label tal cual (no remover bullet)
  const formattedLabel = useMemo(() => label, [label]);

  const titleText = typeof formattedLabel === "string" ? formattedLabel : undefined;
  
  const textColor = textColors[type as NonNullable<ChipType>] ?? textColors.primary;
// debugger;
  // 🔑 Hacer que el contenido herede el color del Chip
  const labelNode =
    tableMode || useTypography ? (
      <Typography
        component="span"
        variant="body2"
        {...typoProps}
        sx={{
          display: "inline",
          color: "inherit",       // << hereda del Chip
          fontSize: "inherit",
          lineHeight: "inherit",
          fontWeight: typoProps?.fontWeight ?? 500,
          letterSpacing: "0.02em",
          ...(typoProps?.sx ?? {}),
        }}
      >
        {formattedLabel as React.ReactNode}
      </Typography>
    ) : (
      <UIText
        text={formattedLabel}
        size="text-xs"
        // color del texto: ahora hereda del Chip (ignora mapping de UIText)
        color={textColor} // no relevante; forzamos inherit vía sx
        sx={{
            width: "100%",
            textAlign: "center",            
            whiteSpace: hasBullet ? "nowrap" : "normal",
            ...sxText,
        }}
      />
    );

  return (
    <ColorChip
      title={titleText}
      label={labelNode}
      icon={icon}
      variant="outlined"
      color={type}
      $hasBullet={hasBullet}
      $tableMode={tableMode}
      $tableLines={tableLines}
      $tableFullWidth={tableFullWidth}
      $tableTextAlign={tableTextAlign}
      $forceOneLineOnMobile={tableForceOneLineOnMobile}
      $bulletWidth={bulletWidth}
    />
  );
};

export default React.memo(UIChip);